'use client';

import { useEffect, useState, useRef, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';

import { LocationSection } from './location-section.component';

import { resetUserLocation } from '@/store/user-location.store';
import { removeUserLocationStorage } from '@/storage/location.storage';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';

interface LocationSectionPortalProps {
  city?: string;
  state?: string;
}

function LocationSectionPortalContent({ city, state }: LocationSectionPortalProps) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams([...Object.values(PersistedUserNavigationSearchParams)]);
  const hasClearedRef = useRef(false);
  const previousPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    setContainer(document.getElementById('header-location-slot'));
  }, []);

  useEffect(() => {
    const isNavigatingToHome = pathname === '/' && previousPathnameRef.current !== '/';
    const hasCidadeParam = searchParams.cidade !== null;

    if (isNavigatingToHome && !hasClearedRef.current && hasCidadeParam) {
      searchParams.removeParams(PersistedUserNavigationSearchParams.CIDADE);
      resetUserLocation();
      removeUserLocationStorage();
      hasClearedRef.current = true;
    }

    if (pathname !== '/') {
      hasClearedRef.current = false;
    }

    previousPathnameRef.current = pathname;
  }, [pathname, searchParams]);

  if (!container) {
    return null;
  }

  return createPortal(<LocationSection city={city} state={state} />, container);
}

export function LocationSectionPortal({ city, state }: LocationSectionPortalProps) {
  return (
    <Suspense fallback={null}>
      <LocationSectionPortalContent city={city} state={state} />
    </Suspense>
  );
}
